import { Create, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useGo, useNavigation } from "@refinedev/core";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  Space,
  Card,
  App,
  Table,
  Upload,
  List,
  Breadcrumb,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  CameraOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useLocation } from "react-router";
import type {
  ControlPointFromAuditFormValues,
  SampleInput,
} from "../../types/control-point";
import { controlPointService } from "../../services/api";
import { ControlPointMapEditor } from "./components/ControlPointMapEditor";
import "./control-points.css";
import { usePageTitle } from "../../hooks";

const { TextArea } = Input;
const { Dragger } = Upload;

interface PhotoItem {
  uid: string;
  file: File;
  description: string;
  preview: string;
}

/**
 * ControlPointCreate Component
 * Form for creating a new control point from an audit context
 * Automatically associates with audit's farm and creates first review
 */
export const ControlPointCreate = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const go = useGo();
  const { show } = useNavigation();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get("auditId");
  const location = useLocation();
  // Geometry pre-loaded from the audit-detail map's draw flow.
  const initialGeometryFromRoute = (location.state as any)
    ?.initialGeometry as GeoJSON.Geometry | undefined;

  // Set page title
  usePageTitle(t("controlPoint.create", "Crear Punto de Control"));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(
    initialGeometryFromRoute ?? null,
  );
  const [referencePlots, setReferencePlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [farmId, setFarmId] = useState<number | null>(null);
  const [samples, setSamples] = useState<SampleInput[]>([]);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);

  const { formProps, form } = useForm<
    ControlPointFromAuditFormValues,
    HttpError,
    ControlPointFromAuditFormValues
  >({
    action: "create",
    redirect: false,
    warnWhenUnsavedChanges: false,
  });

  // Load reasons for select dropdown
  const { selectProps: reasonSelectProps } = useSelect({
    resource: "suspicious-crop-reasons",
    optionLabel: "name",
    optionValue: "id",
  });

  // Validate that we have an audit ID and load farm data
  useEffect(() => {
    if (!auditId) {
      message.error(
        t(
          "controlPoint.error.noAuditId",
          "No se especificó una auditoría. Por favor, cree el punto de control desde el panel de auditoría."
        )
      );
      go({ to: "/audits" });
      return;
    }

    let isMounted = true;

    // Load audit data to get farm ID
    const loadAuditData = async () => {
      try {
        const { apiClient } = await import("../../services/api/apiClient");
        const response = await apiClient.get(`/audits/${auditId}/`);
        const audit = response.data;
        
        if (!isMounted) return;
        
        if (audit.farm) {
          setFarmId(audit.farm);

          // Pull every polygon-bearing feature for this audit (its
          // audit plots + the farm's cadastral plots + any existing
          // colindantes) from the unified `map_data` endpoint, then
          // hand it to the map editor as `referencePlots`. Using
          // map_data covers the common case where the auditor opens
          // the control-point form before they've created any audit
          // plots — without this fallback the map centres on Spain
          // because `getPlotsByAudit` returns an empty list.
          try {
            const mapDataResponse = await apiClient.get<{
              type: "FeatureCollection";
              features: Array<{
                type: "Feature";
                geometry: GeoJSON.Geometry;
                properties: any;
              }>;
            }>(`/audits/${auditId}/map_data/`);

            if (!isMounted) return;

            const all = mapDataResponse.data?.features ?? [];
            const isPolygon = (g: GeoJSON.Geometry | undefined) =>
              g?.type === "Polygon" || g?.type === "MultiPolygon";
            // Reference layer preference: the farm's cadastral
            // plots first (they're the canonical perimeter the
            // auditor walks). Only fall back to audit plots when
            // the farm has no cadastral geometry — in that case
            // the auditor's own polygons are the best available
            // frame. We intentionally do NOT mix the two layers
            // so the map doesn't end up with duplicated polygons.
            const farmPolygons = all.filter(
              (f) =>
                isPolygon(f.geometry) &&
                f.properties?.layer_type === "farm_plot",
            );
            const referenceSource =
              farmPolygons.length > 0
                ? farmPolygons
                : all.filter(
                    (f) =>
                      isPolygon(f.geometry) &&
                      f.properties?.layer_type === "audit_plot",
                  );
            const features = referenceSource.map((f) => ({
              type: "Feature" as const,
              geometry: f.geometry,
              properties: {
                name: f.properties?.name,
                surface: f.properties?.surface,
                layer_type: f.properties?.layer_type,
              },
            }));

            if (features.length > 0) {
              setReferencePlots({
                type: "FeatureCollection",
                features,
              });
            }
          } catch (error) {
            console.error("Error loading audit map data:", error);
          }
        }
      } catch (error) {
        console.error("Error loading audit data:", error);
      }
    };

    loadAuditData();

    return () => {
      isMounted = false;
    };
  }, [auditId]); // Removed message, t, go from dependencies to avoid re-renders

  // Set default values
  useEffect(() => {
    form.setFieldsValue({
      is_producer_owner: false,
      estimated_surface: 0,
    });
  }, [form]);

  /**
   * Update form when geometry changes
   */
  useEffect(() => {
    form.setFieldValue("geom", geometry);
  }, [geometry, form]);

  /**
   * Superficie del polígono dibujado, en hectáreas. Aproximación esférica
   * (fórmula de Green sobre WGS84) — mismo cálculo que en el formulario
   * de parcelas (`plot-form.tsx`), sub-percent de error a escala de finca.
   * Devuelve null si no hay geometría o es un punto/línea.
   */
  const polygonAreaHa = useMemo(() => {
    if (!geometry) return null;
    const coordsList: number[][][] = [];
    if (geometry.type === "Polygon") {
      coordsList.push(geometry.coordinates[0] as unknown as number[][]);
    } else if (geometry.type === "MultiPolygon") {
      for (const poly of geometry.coordinates as any[]) {
        coordsList.push(poly[0]);
      }
    } else {
      return null;
    }
    const R = 6378137; // WGS84 equatorial radius (m)
    const toRad = (d: number) => (d * Math.PI) / 180;
    let totalM2 = 0;
    for (const ring of coordsList) {
      let area = 0;
      for (let i = 0; i < ring.length - 1; i++) {
        const [lon1, lat1] = ring[i];
        const [lon2, lat2] = ring[i + 1];
        area += (toRad(lon2) - toRad(lon1)) *
          (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
      }
      totalM2 += Math.abs((area * R * R) / 2);
    }
    return totalM2 / 10000;
  }, [geometry]);

  /**
   * Add a new sample row
   */
  const handleAddSample = () => {
    setSamples([...samples, { code_sample: "", position_waypoint: "" }]);
  };

  /**
   * Remove a sample row
   */
  const handleRemoveSample = (index: number) => {
    setSamples(samples.filter((_, i) => i !== index));
  };

  /**
   * Update a sample field
   */
  const handleSampleChange = (index: number, field: keyof SampleInput, value: string) => {
    const newSamples = [...samples];
    newSamples[index] = { ...newSamples[index], [field]: value };
    setSamples(newSamples);
  };

  /**
   * Validate image file type and size
   */
  const validateFile = (file: File): boolean => {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Solo se permiten archivos JPG, JPEG y PNG");
      return false;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      message.error("El tamaño del archivo no puede exceder 10MB");
      return false;
    }

    return true;
  };

  /**
   * Handle file selection from Upload component
   */
  const handleFileChange = (info: any) => {
    const { fileList } = info;

    // Process new files
    const newPhotos: PhotoItem[] = [];

    fileList.forEach((file: UploadFile) => {
      if (file.originFileObj) {
        // Validate file
        if (!validateFile(file.originFileObj)) {
          return;
        }

        // Create preview URL
        const preview = URL.createObjectURL(file.originFileObj);

        newPhotos.push({
          uid: file.uid,
          file: file.originFileObj as File,
          description: "",
          preview,
        });
      }
    });

    setPhotos(newPhotos);
  };

  /**
   * Handle description change for a photo
   */
  const handleDescriptionChange = (uid: string, description: string) => {
    setPhotos((prevPhotos) =>
      prevPhotos.map((photo) =>
        photo.uid === uid ? { ...photo, description } : photo
      )
    );
  };

  /**
   * Remove a photo from the list
   */
  const handleRemovePhoto = (uid: string) => {
    setPhotos((prevPhotos) => {
      const photoToRemove = prevPhotos.find((p) => p.uid === uid);
      if (photoToRemove) {
        // Revoke preview URL to free memory
        URL.revokeObjectURL(photoToRemove.preview);
      }
      return prevPhotos.filter((p) => p.uid !== uid);
    });
  };

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: ControlPointFromAuditFormValues) => {
    if (!auditId) {
      message.error(
        t("controlPoint.error.noAuditId", "No se especificó una auditoría")
      );
      return;
    }

    try {
      setIsSubmitting(true);

      // Convert photos to base64
      const photosBase64 = await Promise.all(
        photos.map(async (photo) => ({
          image_base64: await fileToBase64(photo.file),
          description: photo.description,
        }))
      );

      // Prepare data
      const data: any = {
        reason: values.reason,
        is_producer_owner: values.is_producer_owner,
        geom: geometry || undefined,
        comment: values.comment,
        estimated_surface: values.estimated_surface || 0,
        samples: samples.filter(s => s.code_sample.trim() !== ''),
        photos: photosBase64,
      };

      // Create control point from audit
      const createdControlPoint = await controlPointService.createFromAudit(
        Number(auditId),
        data
      );

      message.success(
        t(
          "controlPoint.create_success",
          "Punto de control creado exitosamente"
        )
      );

      // Clean up preview URLs
      photos.forEach((photo) => {
        URL.revokeObjectURL(photo.preview);
      });

      // Navigate to audit detail page
      go({ to: `/audits/${auditId}` });
    } catch (error: any) {
      message.error(
        error.message ||
          t("controlPoint.create_error", "Error al crear el punto de control")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!auditId) {
    return null;
  }

  // Sample table columns
  const sampleColumns = [
    {
      title: "Código de Laboratorio",
      dataIndex: "code_sample",
      key: "code_sample",
      render: (_: any, sample: SampleInput, index: number) => (
        <Input
          value={sample.code_sample}
          onChange={(e) => handleSampleChange(index, 'code_sample', e.target.value)}
          placeholder="Ej: LAB001"
        />
      ),
    },
    {
      title: "Número Waypoint",
      dataIndex: "position_waypoint",
      key: "position_waypoint",
      render: (_: any, sample: SampleInput, index: number) => (
        <Input
          value={sample.position_waypoint}
          onChange={(e) => handleSampleChange(index, 'position_waypoint', e.target.value)}
          placeholder="Ej: WP001"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      render: (_: any, __: SampleInput, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveSample(index)}
        />
      ),
    },
  ];

  return (
    <Create
      title={t("controlPoint.create", "Crear Punto de Control")}
      breadcrumb={
        <Breadcrumb
          items={[
            {
              title: "Auditorías",
              href: "/audits",
            },
            {
              title: auditId ? `Auditoría #${auditId}` : "Auditoría",
              href: auditId ? `/audits/${auditId}` : "/audits",
            },
            {
              title: "Crear Punto de Control",
            },
          ]}
        />
      }
      saveButtonProps={{
        loading: isSubmitting,
        onClick: () => {
          form.submit();
        },
      }}
      headerButtons={
        // Top-right "Volver" — mirrors the audit detail's back
        // button so the auditor can leave without scrolling to
        // the footer.
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            auditId ? go({ to: `/audits/${auditId}` }) : go({ to: "/audits" })
          }
        >
          {t("common.back", "Volver")}
        </Button>
      }
      footerButtons={
        // Bottom: "Cancelar" + "Guardar". Cancelar mirrors the
        // top-right Volver action (navigate away without saving)
        // but uses the "cancel" wording that's more appropriate
        // next to a save button.
        <Space>
          <Button
            onClick={() =>
              auditId
                ? go({ to: `/audits/${auditId}` })
                : go({ to: "/audits" })
            }
          >
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isSubmitting}
            onClick={() => form.submit()}
          >
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      }
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Two-column layout: form on the left, sticky map on the
            right. On screens narrower than lg the columns stack so
            the map ends up below the form. */}
        <Row gutter={24}>
          <Col xs={24} lg={15}>
        <Card
          title={t("controlPoint.basicInfo", "Información Básica")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                label={t("controlPoint.reason", "Motivo")}
                name="reason"
                rules={[
                  {
                    required: true,
                    message: `${t("controlPoint.reason")} ${t("common.required")}`,
                  },
                ]}
              >
                <Select
                  {...reasonSelectProps}
                  placeholder={t(
                    "controlPoint.reasonPlaceholder",
                    "Seleccione el motivo"
                  )}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={t(
                  "controlPoint.isProducerOwner",
                  "¿Pertenece al Productor?"
                )}
                name="is_producer_owner"
                valuePropName="checked"
                tooltip={t(
                  "controlPoint.isProducerOwnerTooltip",
                  "Indica si el cultivo sospechoso pertenece al productor auditado"
                )}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t("controlPoint.estimatedSurface", "Superficie Estimada (ha)")}
                name="estimated_surface"
                tooltip={t(
                  "controlPoint.estimatedSurfaceTooltip",
                  "Superficie estimada del cultivo sospechoso en hectáreas"
                )}
                extra={
                  polygonAreaHa != null ? (
                    <span>
                      {t("plot.polygon_area_hint", "Polígono dibujado")}:{" "}
                      <strong>{polygonAreaHa.toFixed(2)} ha</strong>{" "}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, height: "auto" }}
                        onClick={() =>
                          form.setFieldValue(
                            "estimated_surface",
                            Number(polygonAreaHa.toFixed(2)),
                          )
                        }
                      >
                        {t("plot.apply_polygon_area", "Usar este valor")}
                      </Button>
                    </span>
                  ) : undefined
                }
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Ej: 2.5"
                  min={0}
                  step={0.1}
                  precision={2}
                  addonAfter="ha"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={t("controlPoint.initialReview", "Revisión Inicial")}
          style={{ marginBottom: 24 }}
        >
          <Form.Item
            label={t("controlPoint.comment", "Comentario")}
            name="comment"
            rules={[
              {
                required: true,
                message: `${t("controlPoint.comment")} ${t("common.required")}`,
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t(
                "controlPoint.commentPlaceholder",
                "Describa el cultivo sospechoso detectado..."
              )}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Card>

        <Card
          title={t("controlPoint.samples", "Muestras")}
          style={{ marginBottom: 24 }}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            {t(
              "controlPoint.samplesNote",
              "Si se tomaron muestras durante la detección, añádalas aquí con su código de laboratorio y número de waypoint."
            )}
          </p>

          <Button
            type="dashed"
            onClick={handleAddSample}
            icon={<PlusOutlined />}
            style={{ marginBottom: 16, width: "100%" }}
          >
            {t("controlPoint.addSample", "Añadir Muestra")}
          </Button>

          {samples.length > 0 && (
            <Table
              dataSource={samples}
              columns={sampleColumns}
              pagination={false}
              rowKey={(_, index) => `sample-${index}`}
              size="small"
            />
          )}
        </Card>

        <Card
          title={
            <Space>
              <CameraOutlined />
              {t("controlPoint.photos", "Fotografías")}
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            {t(
              "controlPoint.photosNote",
              "Añada fotografías del cultivo sospechoso detectado. Puede añadir una descripción a cada foto."
            )}
          </p>

          {/* File Upload Section */}
          {photos.length === 0 && (
            <Dragger
              multiple
              accept="image/jpeg,image/jpg,image/png"
              beforeUpload={() => false} // Prevent auto upload
              onChange={handleFileChange}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t("photo.drag_drop") ||
                  "Haga clic o arrastre archivos a esta área para cargar"}
              </p>
              <p className="ant-upload-hint">
                {t("photo.upload_hint") ||
                  "Soporta carga múltiple. Solo archivos JPG, JPEG y PNG. Tamaño máximo: 10MB por archivo."}
              </p>
            </Dragger>
          )}

          {/* Photo List Section */}
          {photos.length > 0 && (
            <>
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <span>
                    {t("photo.selected_photos") || "Fotos seleccionadas"}:{" "}
                    <strong>{photos.length}</strong>
                  </span>
                  <Button
                    size="small"
                    onClick={() => {
                      photos.forEach((photo) => {
                        URL.revokeObjectURL(photo.preview);
                      });
                      setPhotos([]);
                    }}
                  >
                    {t("photo.clear_all") || "Limpiar todo"}
                  </Button>
                </Space>
              </div>

              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 3 }}
                dataSource={photos}
                renderItem={(photo) => (
                  <List.Item>
                    <Card
                      size="small"
                      cover={
                        <div
                          style={{
                            height: 200,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0",
                          }}
                        >
                          <img
                            alt={photo.file.name}
                            src={photo.preview}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      }
                      actions={[
                        <Button
                          key="delete"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemovePhoto(photo.uid)}
                        >
                          {t("common.delete") || "Eliminar"}
                        </Button>,
                      ]}
                    >
                      <Card.Meta
                        title={
                          <div
                            style={{
                              fontSize: 12,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={photo.file.name}
                          >
                            {photo.file.name}
                          </div>
                        }
                        description={
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 500 }}>
                              {t("photo.description") ||
                                "Descripción (opcional)"}
                            </label>
                            <TextArea
                              rows={2}
                              placeholder={
                                t("photo.description_placeholder") ||
                                "Agregar descripción..."
                              }
                              value={photo.description}
                              onChange={(e) =>
                                handleDescriptionChange(
                                  photo.uid,
                                  e.target.value
                                )
                              }
                              maxLength={500}
                            />
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </>
          )}
        </Card>
          </Col>

          <Col xs={24} lg={9}>
            <div style={{ position: "sticky", top: 24 }}>
              <Card
                title={
                  <Space>
                    <EnvironmentOutlined />
                    {t("controlPoint.location", "Ubicación")}
                  </Space>
                }
                style={{ marginBottom: 24 }}
                styles={{ body: { padding: 12 } }}
              >
                <p
                  style={{
                    marginBottom: 12,
                    color: "#666",
                    fontSize: 12,
                  }}
                >
                  {t(
                    "controlPoint.locationNoteShort",
                    "Marca un punto o dibuja un polígono. Las parcelas auditadas aparecen en azul.",
                  )}
                </p>
                <div
                  style={{
                    height: 460,
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <ControlPointMapEditor
                    geometry={geometry}
                    onGeometryChange={setGeometry}
                    editable={true}
                    referencePlots={referencePlots}
                  />
                </div>
                {geometry && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: "#52c41a",
                    }}
                  >
                    ✓{" "}
                    {geometry.type === "Point"
                      ? t("controlPoint.pointMarked", "Punto marcado")
                      : t("controlPoint.polygonDrawn", "Polígono dibujado")}
                  </div>
                )}
                {/* Hidden field for geom */}
                <Form.Item name="geom" hidden>
                  <Input />
                </Form.Item>
              </Card>
            </div>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
